import { getConfig } from '@/config/config';
import type { ApiErrorBody } from './types';
import { rebaseMediaUrls } from './mediaUrl';
import { tNow } from '@/i18n/languageStore';

/**
 * The REST client (tasks.md T050; reference mobile/src/services/apiClient.ts).
 *
 * The web session lives in an httpOnly cookie the page cannot read, so every
 * request is sent with `credentials: 'include'`. Every change is marked
 * `X-Kindra-Client: web`, required by the backend for cookie-authenticated
 * changes (its CSRF rule, contracts/backend-changes.md §2); reads are not, so
 * they need no CORS preflight (tasks.md T205).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** A 400 `validation` answer lists every bad field, so a form can show each one next to its field. */
    readonly fieldErrors: { field: string; reason: string }[] = [],
  ) {
    super(tNow(message));
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type RequestObserver = (method: Method, path: string) => void;
type SessionRejectedListener = (details: { message: string }) => void;

const observers = new Set<RequestObserver>();
const sessionRejectedListeners = new Set<SessionRejectedListener>();

/** A 401 on these means wrong credentials, not an ended session. */
const CREDENTIAL_PATHS = ['/auth/login', '/auth/logout', '/auth/password-reset'];

/** Calls `observer` after every successful request (e.g. to notice which features are used). */
export function observeRequests(observer: RequestObserver): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

/** Called when the server rejects the session: expired, or the member was suspended, removed or deleted. */
export function onSessionRejected(listener: SessionRejectedListener): () => void {
  sessionRejectedListeners.add(listener);
  return () => sessionRejectedListeners.delete(listener);
}

async function send<T>(method: Method, path: string, body?: BodyInit, json = false): Promise<T> {
  const { apiUrl } = getConfig();
  // Reads stay "simple" cross-origin requests (no custom header), so the browser sends them
  // without an OPTIONS preflight first; changes carry the header the backend's CSRF rule needs.
  const headers: Record<string, string> = method === 'GET' ? {} : { 'X-Kindra-Client': 'web' };
  if (json) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`${apiUrl}${path}`, { method, headers, body, credentials: 'include' });
  } catch {
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      'Kindra could not be reached. Check your connection and try again.',
    );
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : undefined;

  if (!response.ok) {
    const error = (payload ?? {}) as Partial<ApiErrorBody>;
    const message = error.message ?? 'Something went wrong. Please try again.';
    if (response.status === 401 && !CREDENTIAL_PATHS.includes(path)) {
      sessionRejectedListeners.forEach((listener) => listener({ message }));
    }
    throw new ApiError(response.status, error.code ?? 'ERROR', message, error.errors ?? []);
  }

  observers.forEach((observer) => {
    try {
      observer(method, path);
    } catch {
      // an observer must never break a request
    }
  });
  return rebaseMediaUrls(payload, apiUrl) as T;
}

const withJson = <T>(method: Method, path: string, body?: unknown) =>
  send<T>(method, path, body === undefined ? undefined : JSON.stringify(body), body !== undefined);

export const api = {
  get: <T>(path: string) => send<T>('GET', path),
  post: <T>(path: string, body?: unknown) => withJson<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => withJson<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => withJson<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => withJson<T>('DELETE', path, body),
  /** Multipart upload; the browser sets the boundary header itself. */
  upload: <T>(path: string, form: FormData) => send<T>('POST', path, form),
};
